import React from 'react';
import InputIconByKey from './InputIconByKey';
import RequiredAsteriskTooltip from './RequiredAsteriskTooltip';
import { IconKey } from '../../../types/system/icons';

interface SearchInputFieldProps {
  label?: string;
  name: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  iconKey?: IconKey;
  rightContent?: React.ReactNode;
  withMargin?: boolean;
  size?: 'sm' | 'md';
  ariaLabel?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}

const SearchInputField: React.FC<SearchInputFieldProps> = ({
  label,
  name,
  value,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  placeholder = 'Buscar...',
  error,
  disabled = false,
  required = false,
  iconKey = 'search',
  rightContent,
  withMargin = true,
  size = 'md',
  ariaLabel,
  inputMode,
}) => {
  const containerClass = `relative ${withMargin ? 'mb-5' : ''}`;

  const containerHeight = size === 'sm' ? 'h-10' : 'h-[50px]';
  const horizontalPadding = size === 'sm' ? 'px-3' : 'px-4';
  const rightPadding = size === 'sm' ? 'pr-3' : 'pr-4';

  return (
    <div className={containerClass}>
      {label && (
        <label className="mb-2.5 flex items-center gap-1 text-sm font-medium text-black dark:text-white">
          {label}
          {required && <RequiredAsteriskTooltip delay={400} />}
        </label>
      )}

      <div
        className={`
          bg-gray-1
          flex
          w-full
          ${containerHeight}
          items-stretch
          overflow-hidden
          rounded-xl
          border
          border-stroke
          transition-all
          duration-200
          focus-within:border-primary
          focus-within:ring-2
          focus-within:ring-primary/10
          dark:border-strokedark
          dark:bg-meta-4
          ${disabled ? 'cursor-not-allowed opacity-70' : ''}
        `}
      >
        {iconKey && (
          <div
            className={`
              flex
              shrink-0
              items-center
              justify-center
              border-r
              border-stroke
              bg-gray-2
              ${horizontalPadding}
              dark:border-strokedark
              dark:bg-boxdark-2
            `}
          >
            <InputIconByKey iconKey={iconKey} />
          </div>
        )}

        <input
          id={name}
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          aria-label={ariaLabel || label}
          inputMode={inputMode}
          className={`
            h-full
            w-full
            bg-transparent
            ${horizontalPadding}
            text-sm
            text-black
            outline-none
            placeholder:text-body
            dark:text-white
            dark:placeholder:text-bodydark2
          `}
        />

        {rightContent && (
          <div
            className={`
              flex
              shrink-0
              items-center
              ${rightPadding}
            `}
          >
            {rightContent}
          </div>
        )}
      </div>

      {error && (
        <p className="mt-2 text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
};

export default SearchInputField;
